// The ONE application menu. The web build's DOM menu bar (renderer/webMenu.ts)
// and the desktop's native Electron menu (main/main.ts) are both BUILT from this
// model, so the two can't drift apart: same menus, same order, same labels, same
// shortcuts. Labels are English (each builder translates them).
//
// A toolbar command appears here as `cmd(buttonId, label)`: its channel is
// `menu:cmd-<buttonId>`, and the renderer answers every such channel by clicking
// that button — the command bar's own path, so a menu item does exactly what the
// toolbar does. `npm test` checks every command in commands.ts is reachable here.
// Pure (no DOM, no Electron).

import type { ShortcutId } from './shortcuts';

export type Build = 'web' | 'desktop';

export type MenuEntry =
  | { sep: true; only?: Build }
  | { label: string; channel: string; shortcut?: ShortcutId; only?: Build }
  | { label: string; submenu: MenuEntry[]; only?: Build }
  /** Desktop: the recent project files, filled in by the builder. */
  | { label: string; recent: true; only: 'desktop' }
  /** A native Electron role (reload, toggleDevTools, …); desktop only. */
  | { role: 'quitOrClose' | 'reload' | 'toggleDevTools' | 'togglefullscreen'; only: 'desktop' };

export interface TopMenu {
  label: string;
  items: MenuEntry[];
  /** The Language menu: each build lists the UI languages itself. */
  languages?: true;
}

/** The channel prefix of a toolbar command in a menu. */
export const CMD_CHANNEL = 'menu:cmd-';

const sep = (only?: Build): MenuEntry => (only ? { sep: true, only } : { sep: true });
/** A toolbar command: clicks the button `id`. */
export const cmd = (id: string, label: string, shortcut?: ShortcutId): MenuEntry =>
  shortcut ? { label, channel: CMD_CHANNEL + id, shortcut } : { label, channel: CMD_CHANNEL + id };

/** Shortcuts the renderer binds itself for both builds (they must also work with
 *  no menu at all); a menu shows their key but must not register it again, or the
 *  action would fire twice. */
export const RENDERER_BOUND = new Set<ShortcutId>(['undo', 'redo', 'cut', 'copy', 'paste', 'duplicate', 'selectAll', 'group', 'ungroup', 'delete', 'help']);

export const MENUS: TopMenu[] = [
  {
    label: 'File',
    items: [
      { label: 'New Drawing', channel: 'menu:newDrawing', shortcut: 'newDrawing' },
      { label: 'Open Project…', channel: 'menu:openProject', shortcut: 'openProject' },
      { label: 'Open Recent', recent: true, only: 'desktop' },
      { label: 'Save Project', channel: 'menu:saveProject', shortcut: 'saveProject', only: 'desktop' },
      { label: 'Save Project As…', channel: 'menu:saveProjectAs', shortcut: 'saveProjectAs', only: 'desktop' },
      { label: 'Save to Cloud', channel: 'menu:saveCloud', shortcut: 'saveProject', only: 'web' },
      { label: 'Save to Cloud As…', channel: 'menu:saveCloudAs', shortcut: 'saveProjectAs', only: 'web' },
      { label: 'Load from Cloud…', channel: 'menu:loadCloud', only: 'web' },
      sep(),
      { label: 'Import Drawing…', channel: 'menu:openDrawing', shortcut: 'importDrawing' },
      { label: 'New Product from Drawing…', channel: 'menu:newProduct' },
      sep(),
      { label: 'Drawing Properties…', channel: 'menu:drawingProperties' },
      {
        label: 'Export',
        submenu: [
          { label: 'PDF (single page)…', channel: 'menu:exportPdf' },
          { label: 'PDF (all pages)…', channel: 'menu:exportPdfAll' },
          { label: 'DXF (2D)…', channel: 'menu:exportDxf' },
          { label: '3D DXF…', channel: 'menu:exportDxf3d' },
          { label: '3D glTF (.glb)…', channel: 'menu:exportGlb' },
          { label: '3D OBJ…', channel: 'menu:exportObj' },
          { label: '3D STL…', channel: 'menu:exportStl' },
          { label: '3D USDZ (AR)…', channel: 'menu:exportUsdz' },
        ],
      },
      sep(),
      { label: 'Print…', channel: 'menu:print', shortcut: 'print' },
      { label: 'Print All Sheets', channel: 'menu:printAll' },
      sep(),
      { label: 'Log out', channel: '@logout', only: 'web' },
      { role: 'quitOrClose', only: 'desktop' },
    ],
  },
  {
    label: 'Edit',
    items: [
      { label: 'Undo', channel: 'menu:undo', shortcut: 'undo' },
      { label: 'Redo', channel: 'menu:redo', shortcut: 'redo' },
      sep(),
      { label: 'Cut', channel: 'menu:cut', shortcut: 'cut' },
      { label: 'Copy', channel: 'menu:copySelection', shortcut: 'copy' },
      { label: 'Paste', channel: 'menu:paste', shortcut: 'paste' },
      { label: 'Duplicate', channel: 'menu:duplicate', shortcut: 'duplicate' },
      { label: 'Select All', channel: 'menu:selectAll', shortcut: 'selectAll' },
      sep(),
      { label: 'Group', channel: 'menu:group', shortcut: 'group' },
      { label: 'Ungroup', channel: 'menu:ungroup', shortcut: 'ungroup' },
      sep(),
      { label: 'Delete Selected', channel: 'menu:delete', shortcut: 'delete' },
      sep(),
      { label: 'Copy Schedule', channel: 'menu:copySchedule' },
    ],
  },
  {
    label: 'View',
    items: [
      { label: '2D Drawing', channel: 'menu:view2d', shortcut: 'view2d' },
      { label: '3D Preview', channel: 'menu:view3d', shortcut: 'view3d' },
      sep(),
      { label: 'Zoom to Fit', channel: 'menu:zoomFit', shortcut: 'zoomFit' },
      cmd('btn-zoom-window', 'Zoom Window'),
      cmd('btn-zoom-in', 'Zoom in'),
      cmd('btn-zoom-out', 'Zoom out'),
      cmd('btn-zoom-selection', 'Zoom to the selection'),
      cmd('btn-view3d', 'Standard views'),
      cmd('btn-isolate-3d', 'Isolate the selection'),
      cmd('btn-isolate-reset-3d', 'Reset isolate'),
      cmd('btn-show-constraints', 'Show constraints'),
      { label: 'Show ceilings (on / off)', channel: 'menu:showCeilings' },
      sep(),
      { label: 'Reset Toolbars', channel: 'menu:resetToolbars' },
      { label: 'Switch toolbar style (sections ⇄ tabs)', channel: 'menu:toolbarStyle' },
      { label: 'Show button names on hover (on / off)', channel: 'menu:toolbarCallouts' },
      sep(),
      { label: 'Reload', channel: '@reload', only: 'web' },
      { label: 'Toggle Fullscreen', channel: '@fullscreen', shortcut: 'fullscreen', only: 'web' },
      { role: 'reload', only: 'desktop' },
      { role: 'toggleDevTools', only: 'desktop' },
      { role: 'togglefullscreen', only: 'desktop' },
    ],
  },
  {
    label: 'Insert',
    items: [
      { label: 'Product from the catalogue…', channel: 'menu:focusCatalog' },
      { label: 'Image or PDF underlay…', channel: 'menu:openDrawing' },
      { label: 'Sheet', channel: 'menu:newSheet' },
      sep(),
      cmd('btn-door', 'Door'),
      cmd('btn-glass', 'Glass'),
      cmd('btn-opening', 'Opening'),
      cmd('btn-inner-wall', 'Inner wall'),
      cmd('btn-bench', 'Bench'),
      cmd('btn-bench-support', 'Bench support'),
      cmd('btn-bench-end', 'Bench end'),
      cmd('btn-bench-round', 'Round bench end'),
      cmd('btn-heater-wall', 'Heater wall'),
      cmd('btn-backrest', 'Backrest'),
      cmd('btn-sculpt-bench', 'Sculpted bench'),
      {
        label: 'Lighting',
        submenu: [
          cmd('btn-light-spot', 'Spot light'),
          cmd('btn-light-sky', 'Starry sky'),
          cmd('btn-light-strip', 'Strip light, shining down'),
          cmd('btn-light-strip-up', 'Strip light, shining up'),
          cmd('btn-light-cove', 'Cove light'),
          cmd('btn-light-channel', 'Channel light'),
        ],
      },
      sep(),
      cmd('btn-connection', 'Connection'),
      {
        label: 'On a sheet',
        submenu: [
          cmd('btn-sch-text', 'Text box'),
          cmd('btn-sch-line', 'Line'),
          cmd('btn-sch-conn', 'Connection'),
          cmd('btn-sch-viewport', 'Viewport'),
          cmd('btn-sch-image', 'Image…'),
          cmd('btn-sch-elec', 'Electrical schematic'),
          cmd('btn-sch-elec-box', 'Schematic box'),
          cmd('btn-sch-elec-wire', 'Schematic wire'),
        ],
      },
    ],
  },
  {
    label: 'Draw',
    items: [
      cmd('btn-line', 'Line'),
      cmd('btn-wall', 'Wall'),
      cmd('btn-rectangle', 'Rectangle'),
      cmd('btn-polygon', 'Polygon'),
      cmd('btn-circle', 'Circle'),
      cmd('btn-arc', 'Arc'),
      sep(),
      {
        label: 'Floor',
        submenu: [
          cmd('btn-floor-tiled', 'Tiled floor'),
          cmd('btn-floor-tiled-slope', 'Sloping tiled floor'),
          cmd('btn-floor-screed', 'Screed'),
          cmd('btn-floor-screed-slope', 'Sloping screed'),
          cmd('btn-floor-grille', 'Floor grille'),
        ],
      },
    ],
  },
  {
    label: 'Annotate',
    items: [
      cmd('btn-dimension', 'Dimension'),
      cmd('btn-measure', 'Measure'),
      sep(),
      cmd('btn-text', 'Text'),
      cmd('btn-leader', 'Leader'),
      cmd('btn-revcloud', 'Revision cloud'),
      sep(),
      cmd('btn-section', 'Section'),
      cmd('btn-section-3d', '3D section'),
      cmd('btn-rcp', 'Reflected ceiling plan'),
      cmd('btn-ceiling-top', 'Plan above the ceiling'),
    ],
  },
  {
    label: 'Modify',
    items: [
      cmd('btn-move', 'Move'),
      cmd('btn-copy', 'Copy'),
      cmd('btn-rotate', 'Rotate'),
      cmd('btn-transform', 'Transform (move · rotate · scale)'),
      cmd('btn-mirror', 'Mirror'),
      sep(),
      cmd('btn-offset', 'Offset'),
      cmd('btn-trim', 'Trim / Extend'),
      cmd('btn-fillet', 'Fillet / Chamfer'),
      sep(),
      cmd('btn-explode', 'Explode'),
      cmd('btn-join', 'Join'),
      cmd('btn-constrain', 'Constrain'),
      cmd('btn-edit-room', 'Edit room'),
      cmd('btn-edit-polygon', 'Edit polygon'),
      sep(),
      cmd('btn-array-rect', 'Rectangular array'),
      cmd('btn-array-polar', 'Polar array'),
      sep(),
      {
        label: 'Order',
        submenu: [
          cmd('btn-front', 'Bring to front'),
          cmd('btn-forward', 'Bring forward'),
          cmd('btn-backward', 'Send backward'),
          cmd('btn-back', 'Send to back'),
        ],
      },
      {
        label: 'Align',
        submenu: [
          cmd('btn-align-left', 'Align left'),
          cmd('btn-align-right', 'Align right'),
          cmd('btn-align-top', 'Align top'),
          cmd('btn-align-bottom', 'Align bottom'),
          sep(),
          cmd('btn-distribute-h', 'Distribute horizontally'),
          cmd('btn-distribute-v', 'Distribute vertically'),
          sep(),
          cmd('btn-attach-left', 'Attach left'),
          cmd('btn-attach-right', 'Attach right'),
          cmd('btn-attach-top', 'Attach top'),
          cmd('btn-attach-bottom', 'Attach bottom'),
        ],
      },
      cmd('btn-flip-h', 'Flip horizontal'),
      cmd('btn-flip-v', 'Flip vertical'),
      sep(),
      cmd('btn-delete', 'Erase', 'delete'),
    ],
  },
  {
    label: 'Tools',
    items: [
      { label: 'Colour Editor…', channel: 'menu:colorEditor' },
      { label: 'Title blocks…', channel: 'menu:cartigli' },
      { label: 'Bench presets…', channel: 'menu:benchPresets' },
      { label: 'Steam bench presets…', channel: 'menu:steamBenchPresets' },
      { label: 'Sauna profiles…', channel: 'menu:saunaProfiles' },
      { label: 'Pipe types…', channel: 'menu:pipeTypes' },
      { label: 'Import Heater…', channel: 'menu:importHeater' },
      { label: 'Update Catalog', channel: 'menu:updateCatalog' },
      { label: 'Deleted products…', channel: 'menu:deletedProducts' },
      sep(),
      cmd('btn-pdf-scale', 'Scale to measure'),
      cmd('btn-pdf-scale-factor', 'Scale an underlay by its drawing scale (1:X)'),
      sep(),
      { label: 'Settings…', channel: 'menu:settings' },
    ],
  },
  { label: 'Language', items: [], languages: true },
  {
    label: 'Help',
    items: [
      { label: 'Keyboard shortcuts', channel: 'menu:shortcuts', shortcut: 'help' },
      { label: 'Tour: coming from AutoCAD', channel: 'menu:tourAutocad' },
      { label: 'Tour: coming from PowerPoint', channel: 'menu:tourPowerpoint' },
      { label: 'Update permissions', channel: '@updatePermissions', only: 'web' },
      sep(),
      { label: 'About StenalCAD', channel: '@about' },
    ],
  },
];

/** The menu as one build shows it: the other build's entries dropped, and no
 *  separator left leading, trailing or doubled. */
export function menusFor(build: Build): TopMenu[] {
  const keep = (items: MenuEntry[]): MenuEntry[] => {
    const out: MenuEntry[] = [];
    for (const it of items) {
      if (it.only && it.only !== build) continue;
      if ('sep' in it) { if (out.length && !('sep' in out[out.length - 1])) out.push(it); continue; }
      out.push('submenu' in it ? { ...it, submenu: keep(it.submenu) } : it);
    }
    while (out.length && 'sep' in out[out.length - 1]) out.pop();
    return out;
  };
  return MENUS.map((m) => ({ ...m, items: keep(m.items) }));
}

/** Every channel the menu can fire (both builds), for wiring the renderer. */
export function menuChannels(items: MenuEntry[] = MENUS.flatMap((m) => m.items)): string[] {
  return items.flatMap((it) => ('submenu' in it ? menuChannels(it.submenu) : 'channel' in it ? [it.channel] : []));
}

/** How many recent project files the desktop keeps. */
export const RECENT_MAX = 8;
/** Put `path` at the top of a recent-files list (no duplicates, capped). */
export function pushRecent(list: string[], path: string): string[] {
  return [path, ...list.filter((p) => p !== path)].slice(0, RECENT_MAX);
}
